/**
 * Measuring tool — click to add waypoints, shows total distance along the path.
 * Lightweight custom implementation using GeoJSON source/layers + haversine.
 * No external dependencies.
 *
 * Uses the global unit system from ../units.ts so the metric/imperial toggle
 * automatically updates measure readouts.
 *
 * Usage:
 *   enableMeasure(map)   — activate measuring mode (add source, layers, handlers)
 *   disableMeasure(map)  — deactivate (remove source, layers, handlers, clear state)
 */
import mapboxgl from 'mapbox-gl';
import { formatDistance, onUnitsChange } from '../units';

// ── Constants ─────────────────────────────────────────────────────────────

const SOURCE_ID = 'measure-source';
const LAYER_LINE = 'measure-line';
const LAYER_POINTS = 'measure-points';

// ── State ─────────────────────────────────────────────────────────────────

let points: [number, number][] = [];
let active = false;

/** Check if the measuring tool is currently active */
export function isMeasureActive(): boolean {
  return active;
}

/** Readout element (created once, reused) */
let readoutEl: HTMLElement | null = null;

// Store handler references for cleanup
let clickHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null;
let dblClickHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null;
let keyHandler: ((e: KeyboardEvent) => void) | null = null;
let mousemoveHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null;
let unsubUnits: (() => void) | null = null;

// ── Haversine distance ────────────────────────────────────────────────────

const R_KM = 6371; // Earth radius in km

function haversineDist(a: [number, number], b: [number, number]): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * sinLng * sinLng;
  return 2 * R_KM * Math.asin(Math.sqrt(h));
}

// ── GeoJSON helpers ───────────────────────────────────────────────────────

function buildGeoJSON(extraPoint?: [number, number]): GeoJSON.FeatureCollection {
  const allPts = extraPoint ? [...points, extraPoint] : points;
  const features: GeoJSON.Feature[] = [];

  // Line
  if (allPts.length >= 2) {
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: allPts },
      properties: {},
    });
  }

  // Points
  for (const pt of allPts) {
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: pt },
      properties: {},
    });
  }

  return { type: 'FeatureCollection', features };
}

function updateSource(map: mapboxgl.Map, extraPoint?: [number, number]) {
  const src = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
  if (src) src.setData(buildGeoJSON(extraPoint));
}

// ── Readout UI ────────────────────────────────────────────────────────────

function ensureReadout(): HTMLElement {
  if (readoutEl) return readoutEl;
  readoutEl = document.createElement('div');
  readoutEl.id = 'measure-readout';
  readoutEl.innerHTML = `
    <span class="measure-distance"></span>
    <button class="measure-clear-btn" title="Clear measurement">✕</button>
  `;
  document.getElementById('map')!.appendChild(readoutEl);
  return readoutEl;
}

/** Last extra point used for live preview (so we can re-render on unit change) */
let lastExtraPoint: [number, number] | undefined;

function updateReadout(extraPoint?: [number, number]) {
  if (!readoutEl) return;
  lastExtraPoint = extraPoint;
  const allPts = extraPoint ? [...points, extraPoint] : points;
  let d = 0;
  for (let i = 1; i < allPts.length; i++) {
    d += haversineDist(allPts[i - 1], allPts[i]);
  }
  const distEl = readoutEl.querySelector('.measure-distance') as HTMLElement;
  if (allPts.length < 2) {
    distEl.textContent = 'Click to add points';
  } else {
    distEl.textContent = formatDistance(d);
  }
}

function showReadout() {
  const el = ensureReadout();
  el.classList.add('visible');
  updateReadout();
}

function hideReadout() {
  if (readoutEl) readoutEl.classList.remove('visible');
}

// ── Clear measurement ─────────────────────────────────────────────────────

function clearMeasurement(map: mapboxgl.Map) {
  points = [];
  updateSource(map);
  updateReadout();
}

// ── Enable / Disable ──────────────────────────────────────────────────────

export function enableMeasure(map: mapboxgl.Map): void {
  if (active) return;
  active = true;

  // Add source + layers
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: buildGeoJSON(),
    });
  }

  if (!map.getLayer(LAYER_LINE)) {
    map.addLayer({
      id: LAYER_LINE,
      type: 'line',
      source: SOURCE_ID,
      paint: {
        'line-color': '#ffffff',
        'line-width': 2.5,
        'line-dasharray': [3, 2],
        'line-opacity': 0.9,
      },
    });
  }

  if (!map.getLayer(LAYER_POINTS)) {
    map.addLayer({
      id: LAYER_POINTS,
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', '$type', 'Point'],
      paint: {
        'circle-radius': 5,
        'circle-color': '#ffffff',
        'circle-stroke-color': '#ef4444',
        'circle-stroke-width': 2,
      },
    });
  }

  // Change cursor
  map.getCanvas().style.cursor = 'crosshair';

  // Click to add point
  clickHandler = (e: mapboxgl.MapMouseEvent) => {
    e.preventDefault();
    points.push([e.lngLat.lng, e.lngLat.lat]);
    updateSource(map);
    updateReadout();
  };
  map.on('click', clickHandler);

  // Live preview line to cursor
  mousemoveHandler = (e: mapboxgl.MapMouseEvent) => {
    if (points.length > 0) {
      updateSource(map, [e.lngLat.lng, e.lngLat.lat]);
      updateReadout([e.lngLat.lng, e.lngLat.lat]);
    }
  };
  map.on('mousemove', mousemoveHandler);

  // Double-click to finish (stop adding)
  dblClickHandler = (e: mapboxgl.MapMouseEvent) => {
    e.preventDefault();
    // Finished — remove the live-preview handler
    if (mousemoveHandler) {
      map.off('mousemove', mousemoveHandler);
      mousemoveHandler = null;
    }
    updateSource(map);
    updateReadout();
  };
  map.on('dblclick', dblClickHandler);

  // Escape to clear
  keyHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      clearMeasurement(map);
    }
  };
  document.addEventListener('keydown', keyHandler);

  // Subscribe to unit changes — re-render readout when units toggle
  unsubUnits = onUnitsChange(() => updateReadout(lastExtraPoint));

  // Wire clear button
  showReadout();
  const clearBtn = readoutEl?.querySelector('.measure-clear-btn');
  if (clearBtn) {
    clearBtn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      clearMeasurement(map);
    });
  }
}

export function disableMeasure(map: mapboxgl.Map): void {
  if (!active) return;
  active = false;

  // Remove handlers
  if (clickHandler) { map.off('click', clickHandler); clickHandler = null; }
  if (dblClickHandler) { map.off('dblclick', dblClickHandler); dblClickHandler = null; }
  if (mousemoveHandler) { map.off('mousemove', mousemoveHandler); mousemoveHandler = null; }
  if (keyHandler) { document.removeEventListener('keydown', keyHandler); keyHandler = null; }
  if (unsubUnits) { unsubUnits(); unsubUnits = null; }

  // Restore cursor
  map.getCanvas().style.cursor = '';

  // Clear data + hide readout
  points = [];
  updateSource(map);
  hideReadout();

  // Remove layers + source
  if (map.getLayer(LAYER_POINTS)) map.removeLayer(LAYER_POINTS);
  if (map.getLayer(LAYER_LINE)) map.removeLayer(LAYER_LINE);
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}
